import type { MaterialTextureConfig } from './product.types';

/**
 * Asset-map dimension slug -> the GLB material slot name(s) a selection in that
 * dimension paints. A product only renders the dimensions its family asset map
 * actually contains, so entries here that a given product/model lacks are simply
 * skipped at render time (see `buildModelMaterials`).
 *
 * Keys MUST be the asset map's dimension slugs (e.g. "frame-finish"), because
 * the selected value is looked up by `slugify(selectorType)`.
 */
export const MATERIAL_SLOTS_BY_DIMENSION: Record<string, string[]> = {
	'seat-fabric': ['mat_seat'],
	'back-fabric': ['mat_fabric'],
	'frame-finish': ['mat_wood'],
	'nailhead-finish': ['mat_nailheads'],
};

/**
 * Real-world tile size (inches) to force for a dimension's texture, overriding
 * the asset map's uv. The map can report a scale that is wrong for the material
 * (e.g. nailhead studs are sub-inch, but the map returns a large default like
 * 10", so a stud samples a near-uniform crop and reads as washed-out). Pinning
 * the repeat here keeps the finish rendering at the size the model expects,
 * independent of the map data. Keyed by asset-map dimension slug.
 */
export const FIXED_REPEAT_INCHES_BY_DIMENSION: Record<
	string,
	{ w: number; h: number }
> = {
	'nailhead-finish': { w: 1, h: 1 },
};

/**
 * Fixed materials always applied to their GLB slot regardless of user selection
 * (felt bottom, black metal, rubber feet, glider frame). These are not
 * selectable dimensions and never appear in the asset map. Keyed by GLB material
 * name; the viewer ignores any slot a given model does not have, so it is safe
 * to include all of them for every model. Texture images ship with the app
 * under /static/materials.
 */
export const STATIC_MATERIALS: Record<string, MaterialTextureConfig> = {
	mat_felt: {
		url: '/static/materials/Bottom_Felt_256_BaseColor.webp',
		repeatWidthInches: 5,
		repeatHeightInches: 5,
	},
	mat_frame: {
		url: '/static/materials/MXT_Glider_Frame_BaseColor.jpg',
		repeatWidthInches: 5,
		repeatHeightInches: 5,
	},
	mat_metalblack: {
		url: '/static/materials/Metal_Black_baseColor.png',
		repeatWidthInches: 5,
		repeatHeightInches: 5,
	},
	mat_rubberfeet: {
		url: '/static/materials/rubber_feet_BaseColor.jpg',
		repeatWidthInches: 5,
		repeatHeightInches: 5,
	},
};
